import { useState, useRef, useEffect } from 'react';
import { parkingApi } from '../services/api';
import { formatApiError } from '../utils/helpers';
import { X, CarSimple, Motorcycle, Bicycle, Check } from '@phosphor-icons/react';

const VEHICLE_ICONS = {
  'Auto': CarSimple,
  'Moto': Motorcycle,
  'Bicicleta': Bicycle
};

export default function CheckInModal({ space, vehicleTypes, onClose, onComplete }) {
  const [step, setStep] = useState(1);
  const [selectedType, setSelectedType] = useState(null);
  const [plateNumber, setPlateNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (step === 2 && inputRef.current) {
      inputRef.current.focus();
    }
  }, [step]);

  const handleTypeSelect = (type) => {
    setSelectedType(type);
    setStep(2);
  };

  const handleSubmit = async () => {
    if (!plateNumber.trim()) {
      setError('Ingrese la patente del vehículo');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await parkingApi.checkIn({
        space_id: space.id,
        plate_number: plateNumber.trim(),
        vehicle_type: selectedType.name
      });
      onComplete(response.data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && plateNumber.trim()) {
      handleSubmit();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50" data-testid="checkin-modal">
      <div className="bg-[#141414] rounded-xl w-full max-w-lg border border-[#27272A]">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[#27272A]">
          <div>
            <h2 className="text-xl font-heading text-white">Entrada - Espacio {space.label}</h2>
            <p className="text-sm text-[#A1A1AA]">{step === 1 ? 'Seleccione tipo de vehículo' : 'Ingrese la patente'}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#27272A] rounded-lg transition-colors" data-testid="checkin-close">
            <X className="w-6 h-6 text-[#A1A1AA]" weight="bold" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {error && (
            <div className="bg-[#450A0A] border border-[#DC2626] text-[#FCA5A5] px-4 py-3 rounded-lg mb-4" data-testid="checkin-error">
              {error}
            </div>
          )}

          {step === 1 ? (
            <div className="grid grid-cols-2 gap-3">
              {vehicleTypes.map((type) => {
                const Icon = VEHICLE_ICONS[type.name] || CarSimple;
                return (
                  <button
                    key={type.id}
                    onClick={() => handleTypeSelect(type)}
                    className="flex flex-col items-center justify-center gap-2 p-6 bg-[#1C1C1E] border border-[#27272A] rounded-xl hover:bg-[#27272A] hover:border-[#16A34A] transition-all touch-target"
                    data-testid={`vehicle-type-${type.name}`}
                  >
                    <Icon className="w-10 h-10 text-white" weight="duotone" />
                    <span className="text-white font-medium">{type.name}</span>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="space-y-4">
              {/* Selected Type */}
              <div className="flex items-center gap-3 p-3 bg-[#1C1C1E] rounded-lg">
                {VEHICLE_ICONS[selectedType?.name] && (
                  (() => {
                    const Icon = VEHICLE_ICONS[selectedType.name];
                    return <Icon className="w-8 h-8 text-[#16A34A]" weight="duotone" />;
                  })()
                )}
                <span className="text-white font-medium">{selectedType?.name}</span>
                <button
                  onClick={() => setStep(1)}
                  className="ml-auto text-[#A1A1AA] hover:text-white text-sm"
                >
                  Cambiar
                </button>
              </div>

              {/* Plate Input */}
              <div>
                <label className="block text-[#A1A1AA] text-sm mb-2 uppercase tracking-wide">Patente</label>
                <input
                  ref={inputRef}
                  type="text"
                  value={plateNumber}
                  onChange={(e) => setPlateNumber(e.target.value.toUpperCase())}
                  onKeyDown={handleKeyDown}
                  className="plate-input w-full h-16 rounded-lg"
                  placeholder="AA-BB-00"
                  maxLength={10}
                  data-testid="plate-input"
                />
              </div>

              {/* Clear Button */}
              {plateNumber && (
                <button
                  onClick={() => setPlateNumber('')}
                  className="text-[#A1A1AA] hover:text-white text-sm"
                >
                  Limpiar
                </button>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {step === 2 && (
          <div className="p-4 border-t border-[#27272A]">
            <button
              onClick={handleSubmit}
              disabled={loading || !plateNumber.trim()}
              className="w-full btn-success text-lg"
              data-testid="checkin-submit"
            >
              {loading ? (
                'Procesando...'
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <Check className="w-5 h-5" weight="bold" />
                  Confirmar Entrada
                </span>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
